import { computed, inject, Injectable } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

import { ApiService } from '@features/api';

import { BlogListingResponse, CustomPage, CustomPageNavigation } from '../models';
import { map } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class CustomPagesService {
  static readonly TOP_LEVEL_SLUGS = new Set(['blog', 'news']);

  #api = inject(ApiService);

  readonly navigation = toSignal(
    this.#api.get<{ data: CustomPageNavigation }>('/navigation').pipe(map(({ data }) => data)),
    {
      initialValue: {
        menu_items: [],
        footer_items: [],
      },
    },
  );

  readonly sortedMenuItems = computed(() => {
    const items = this.navigation().menu_items;
    return [...items].sort((a, b) => {
      const aIsTopLevel = CustomPagesService.TOP_LEVEL_SLUGS.has(a.slug) ? 1 : 0;
      const bIsTopLevel = CustomPagesService.TOP_LEVEL_SLUGS.has(b.slug) ? 1 : 0;
      return aIsTopLevel - bIsTopLevel;
    });
  });

  static navRoute(slug: string): string[] {
    return CustomPagesService.TOP_LEVEL_SLUGS.has(slug) ? ['/', slug] : ['/pages', slug];
  }

  page(slug: string) {
    return this.#api.get<{ data: CustomPage }>(`/custom-pages/${slug}`).pipe(map(({ data }) => data));
  }

  blogListing(pageNumber: number = 1) {
    const path = pageNumber > 1 ? `/blog/page/${pageNumber}` : '/blog';
    return this.#api.get<BlogListingResponse>(path);
  }

  newsListing(pageNumber: number = 1) {
    const path = pageNumber > 1 ? `/news/page/${pageNumber}` : '/news';
    return this.#api.get<BlogListingResponse>(path);
  }

  blogPost(slug: string) {
    return this.#api.get<{ data: CustomPage }>(`/blog/${slug}`).pipe(map(({ data }) => data));
  }

  newsPost(slug: string) {
    return this.#api.get<{ data: CustomPage }>(`/news/${slug}`).pipe(map(({ data }) => data));
  }
}
